import { Enums } from "../thor.enums";
import { onlineT } from "../typings/online";
import { Fellowship, Membership } from "../typings/organization";
import { CategoryObject } from "./categories";
import { PremiumStatus, RefusalReasons } from "./common";
import { LocaleCode } from "./lang";
import {
    ExhibitionTranslations,
    ItemTranslations,
    SaleTranslations,
    Translations
} from "./translations";

export namespace thor {
    export interface Organization {
        id: number;
        created: string;
        updated: string;
        login: string;
        names: OrganizationTypes;
        description?: string;
        type: string;
        agreement: string;
        fellowship: keyof typeof Fellowship;
        membership: Membership;
        url?: string;
        status: OrganizationStatus;
        estimates: OrganizationEstimates;
        options: OrganizationOptions;
        address: OrganizationAddress;
        authorized_sites: Enums.Site[];
        medias: OrganizationMedias;
        shipping?: Shipping;
        $filterPropertiesWithResult?: any[];
        atlas_max_tokens?: number;
        lang: string;
        preferred_currency: Enums.Currency;
        atlas_ba_access_max_tokens?: number;
    }

    export type OrganizationAddress = Address &
        AddressContact & {
            name?: string;
        };

    export interface OrganizationEstimates {
        accept?: boolean;
        advert?: boolean;
        text?: string;
    }

    export interface OrganizationMedias {
        organization_logo: Media;
    }

    export interface OrganizationOptions {
        taxes: OrganizationTypes;
        commission_rate: CommissionRates;
    }

    export interface OrganizationStatus {
        archive: boolean;
        restricted: boolean;
    }

    export interface OrganizationTypes {
        judicial: string;
        voluntary: string;
    }

    export interface CommissionRates {
        judicial: number;
        voluntary: number;
    }

    export interface Media {
        id: number;
        url: string;
    }

    export type Shipping = {
        withdrawal_conditions: string;
        shipping_methods?: ShippingMethod[];
        shipping_partners?: ShippingPartner[];
    };

    export interface ShippingContact {
        email: string;
        telephone: string;
        url?: string;
    }

    export type ShippingMethod = {
        contact: ShippingContact;
        name: string;
        description: string;
        is_organization?: boolean;
    };

    export interface ShippingPartner {
        name: string;
    }

    export enum YearQuota {
        Current = "current_year",
        Next = "next_year"
    }

    export type Quotas = {
        [year in YearQuota]: Quota;
    };

    export type SaleGet = SaleTranslations & {
        id: number;
        name: string;
        description: string;
        category: any;
        items_count: number;
        organization: any;
        meta: {
            live?: SaleMetaLive;
        };
        options: {
            commission_rate: CommissionRates;
        };
        states: SaleStates;
        status: Enums.SaleStatus;
        category_id: number;
        currency: Enums.Currency;
        datetime: string;
        first_published: string;
        last_updated: string;
        address: SaleAddress;
        contact?: Contact;
        exhibitions: Exhibition[];
        contract_id?: number;
        sites: SaleSites;
        sale_version_id: number;
        sale_catalog?: Media;
        sale_conditions?: Media;
        sale_results?: Media;
        shipping_conditions?: Media;
        shipping?: Shipping;
        start_at: string;
        isPast?: boolean;
        saleStarted?: boolean;
        pictures_credits?: string;
        source_lang: LocaleCode;
        startedToday?: boolean;
    };

    export interface SaleMetaLive {
        hasStarted?: boolean;
        hasEnded?: boolean;
        sites: SaleSites;
    }

    export interface SaleSites {
        [Enums.Site.Auction]: {
            is_present: boolean;
            type: Enums.AuctionType.Classic | null;
        };
        [Enums.Site.Interencheres]: {
            is_present: boolean;
            type: Enums.AuctionType | null;
        };
    }

    export interface Contact {
        name: string;
        type: string;
        contacts?: {
            email?: string;
            phone_number?: string;
            fax_number?: string;
        };
    }

    export type Exhibition = ExhibitionTranslations & {
        slots: ExhibitionSlot[];
        address: Address;
        description?: string;
        description_translations: Translations;
    };

    export interface ExhibitionSlot {
        datetime: string;
        duration: number;
    }

    export interface SaleStates {
        hidden?: boolean;
        deleted?: boolean;
        canceled?: boolean;
    }

    export interface SaleMedias {
        sale_results?: Object;
        sale_catalog?: Object;
    }

    export interface SalePost {
        name: string;
        datetime?;
        category_id?: number;
        currency?: Enums.Currency;
        timezone?: string;
    }

    export interface UserLocation {
        address: string;
        postcode: string;
        city: string;
        country: string;
    }

    export interface UserPhone {
        prefix?: string;
        number?: string;
    }

    export interface UserProCompany {
        country: string;
        name: string;
        vat: string;
        status?: string;
        siret?: string;
    }

    export interface OnlineContainersCount {
        count?: number;
        total?: number;
    }

    export type ItemGet = ItemPremium &
        ItemTranslations &
        InViewPort & {
            id: number;
            item_version_id?: number;
            media_count: number;
            description: string;
            meta?: ItemMeta;
            states: ItemStates;
            internal_reference?: string;
            features: ItemFeatures;
            type: string;
            pricing: ItemPricing;
            highlight: boolean;
            category: CategoryObject;
            status: Enums.ItemStatus;
            sale: ItemSale;
            external_reference?: string;
            filtered?: boolean;
            last_updated?: Date;
            medias: MediaGet[];
            online?: {
                end_at: string;
            };
            suppressed?: boolean;
            secretOrderCount?: number; // this property is added afterwards by requesting Live
            valid?: boolean; // this property is added after fetching online items
        };

    export interface ItemPost {
        description?: string;
        meta?: ItemMeta;
        states?: ItemStates;
        internal_reference?: string;
        features?: ItemFeatures;
        type?: string;
        pricing?: ItemPricing;
        highlight?: boolean;
        category_id?: number;
        external_reference?: string;
    }

    /**
     * GET items from online-api: interface
     */
    export interface OnlineItemGet {
        id: number;
        contract_id: number;
        has_caution: boolean;
        caution: number;
        sale_id: number;
        starting_price: number;
        reserve_price: number;
        organization_id: number;
        updated: string;
        created: string;
    }

    export interface ItemPatch {
        asked_for_premium?: boolean;
        description?: string;
        description_translations?: Translations;
        meta?: ItemMeta;
        states?: ItemStates;
        internal_reference?: string;
        features?: ItemFeatures;
        type?: string;
        pricing?: ItemPricing;
        highlight?: boolean;
        category_id?: number;
    }

    export interface ItemMeta {
        live?: string;
        order_number?: ItemMetaOrderNumber;
    }

    export interface ItemMetaOrderNumber {
        primary?: number;
        secondary?: string;
    }

    // @TODO to be completed (no urgency)
    // Sale is hydrated on items only on Search (never on Thor)
    export interface ItemSale {
        datetime: string;
    }

    export type ItemStates = {
        [key in Enums.ItemState]?: boolean;
    };

    export interface ItemFeatures {
        height?: number;
        width?: number;
        depth?: number;
    }

    export interface ItemPricing {
        estimates?: ItemPricingEstimates;
        auctioned?: ItemPricingAuctioned;
        is_confirmed?: boolean;
        caution?: number;
        starting_price?: number;
        reserve_price?: number;
        last_bid?: onlineT.LastBid;
    }

    export interface ItemPricingEstimates {
        min?: number;
        max?: number;
    }

    export interface ItemPricingAuctioned {
        sold: boolean;
        price?: number;
    }

    export interface RewriteUrlImgParameters {
        width?: number;
        height?: number;
        cropMode?: "smart" | "fullFitIn";
        transform?: MediaTransform;
    }

    export interface ToasterParameter {
        message: string;
        warn?: boolean;
        position?: "bottom left" | "bottom right" | "top left" | "top right";
        delay?: number;
        template?: string;
        values?: any;
        hideCloseButton?: boolean;
    }

    export type SaleAddress = Address &
        AddressContact & {
            secret?: string;
        };

    export interface LocationCoordinates {
        lat: number;
        lon: number;
    }

    export interface SearchFilterParams {
        id?: number;
        query?: string;
        from?: string;
        to?: string;
        datetime_range?: string;
        mysales?: 1 | null;
        field?: string;
        category?: string;
        pricing_lower?: number;
        pricing_upper?: number;
        estimates_lower?: number;
        estimates_upper?: number;
        auctioned?: 1 | null;
        highlight?: "1" | null;
        ids?: string;
        site?: Enums.Site | string;
        sites?: {
            [site in Enums.Site]: {
                type: string;
            };
        };
        undated?: number;
        scheduled?: number;
        published?: number;
        is_premium?: "1" | null;
        organization?: string;
        premium_count?: "1" | null;
    }

    export interface SearchOrganizationFilterParams {
        authorized_site?: Enums.Site | string;
    }

    export interface SecretOrderLiveLegacy {
        count_secret_orders: number;
        item_id: number;
    }

    export interface SecretOrdersLiveLegacy {
        [itemId: number]: SecretOrderLiveLegacy;
    }

    export interface SortOrderNumberParameter {
        primary: number;
        secondary: string | number;
        id: number;
    }

    export interface SortParams {
        field: string;
        reverse: boolean;
    }

    export interface SortType {
        field: string;
        label: LabelType;
        sortable?: boolean;
    }

    interface LabelType {
        i18n: string;
        values?: any;
    }
}

export interface Address {
    city?: string;
    country?: string;
    formatted_address?: string;
    postcode?: string;
    location?: thor.LocationCoordinates;
    street?: string;
    street_number?: string;
    route?: string;
}

export interface AddressContact {
    email?: string;
    email_accountant?: string;
    email_facturation?: string;
    telephone?: string;
}

export interface InViewPort {
    inView?: boolean;
    viewportDisplay?: { card: boolean; placeholderImg?: boolean }; // deprecated
}

export interface ItemPremium {
    asked_for_premium?: boolean;
    premium_refused_reasons?: RefusalReasons[];
    premium_status?: PremiumStatus;
}

export enum MediaType {
    ItemPicture = "item_picture",
    ItemVideo = "item_video"
}

export interface MediaMeta {
    description?: string;
    name: string;
    transform?: MediaTransform;
    video_embed?: string;
}

export interface MediaTransform {
    brightness?: number;
    contrast?: number;
    crop?: {
        top: number;
        left: number;
        right: number;
        bottom: number;
    };
    rotate?: number;
    saturation?: number;
    sharpen?: {
        amount: number;
        radius: number;
    };
}

export interface MediaGet {
    id: number;
    meta: MediaMeta;
    rank: number;
    updated: string;
    url: string;
}

export interface MediaUpsert {
    media_id?: number;
    meta?: MediaMeta;
    rank?: number;
    type?: MediaType;
}

export interface Quota {
    consumed: number;
    quota: number;
}

export interface SalePublishRes {
    published_sale_id: number;
    published_items_ids: number[];
}
